import React from "react"
import { GetStaticProps, NextPage } from "next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { StructuredText } from "react-datocms"
import styled from "styled-components"

import { MainLayout } from "../components/layouts/main-layout/MainLayout"
import { SingleColLayout } from "../components/single-col-layout/SingleColLayout"
import { nextLocaleToDatoLocale } from "../datocms/dato-utils"
import { datoClient } from "../datocms/gql/client/apollo-client"
import {
  GetPrivacyPageDocument,
  GetPrivacyPageQuery,
  GetPrivacyPageQueryVariables,
} from "../datocms/gql/generated/graphql"
import {
  headingRenderRule,
  linkRenderRule,
  paragraphRenderRule,
} from "../datocms/nodeRules"
import { NEXT_JS_ISR_REVALIDATE } from "../util/page"
import { theme } from "../util/styles/theme"

export const getStaticProps: GetStaticProps = async ({ locale, preview }) => {
  const response = await datoClient(preview).query<
    GetPrivacyPageQuery,
    GetPrivacyPageQueryVariables
  >({
    query: GetPrivacyPageDocument,
    variables: {
      locale: nextLocaleToDatoLocale(locale),
    },
  })

  if (!response.data.privacyPage) {
    return { notFound: true, revalidate: NEXT_JS_ISR_REVALIDATE }
  }
  return {
    props: {
      ...(await serverSideTranslations(locale, ["common"])),
      page: response.data.privacyPage,
      titleSuffix: response.data.site.globalSeo.titleSuffix,
    },
    revalidate: NEXT_JS_ISR_REVALIDATE,
  }
}

const Link = styled.a`
  text-decoration: none;
  color: ${theme.colors.orange};
`

const renderRules = [
  paragraphRenderRule({
    size: "medium",
  }),
  headingRenderRule({
    1: { size: "xlarge", weight: "bold" },
    2: { size: "xlarge", weight: "medium" },
    3: { size: "large", weight: "medium" },
    4: { size: "medium", weight: "medium" },
    5: { size: "small", weight: "medium" },
    6: { size: "xsmall", weight: "medium" },
  }),
  linkRenderRule(Link),
]

interface PrivacyProps {
  page: GetPrivacyPageQuery["privacyPage"]
  titleSuffix: string
}
const Privacy: NextPage<PrivacyProps> = ({ page, titleSuffix }) => {
  return (
    <MainLayout
      type="metaTags"
      titleSuffix={titleSuffix}
      topBackground="roseGoldLight"
      bottomBackground="roseGoldLight"
      metaTags={page.seoMetaTags}
    >
      <SingleColLayout colProps={{ sm: 10, smOffset: 1, xs: 12 }}>
        <StructuredText data={page.content} customNodeRules={renderRules} />
      </SingleColLayout>
    </MainLayout>
  )
}

export default Privacy
